import { motion } from 'framer-motion';
import {
  FaMobile,
  FaShoppingCart,
  FaVolumeOff,
  FaClipboard,
  FaShieldAlt,
  FaExternalLinkAlt,
  FaGithub
} from 'react-icons/fa';

const Projects = () => {
  const projects = [
    {
      id: 1,
      title: 'Expense Tracker App',
      description: 'A comprehensive expense tracking mobile application built with Flutter and Firebase.',
      technologies: ['Flutter', 'Dart', 'Firebase'],
      icon: FaMobile,
      gradient: 'linear-gradient(135deg, #10b981, #3b82f6)'
    },
    {
      id: 2,
      title: 'E-commerce Mobile App',
      description: 'Full-featured e-commerce application with product catalog and payment integration.',
      technologies: ['Flutter', 'Dart', 'Firebase'],
      icon: FaShoppingCart,
      gradient: 'linear-gradient(135deg, #8b5cf6, #ec4899)'
    },
    {
      id: 3,
      title: 'Mute Tab Extension',
      description: 'Browser extension that allows users to quickly mute/unmute tabs with a single click.',
      technologies: ['JavaScript', 'HTML', 'CSS'],
      icon: FaVolumeOff,
      gradient: 'linear-gradient(135deg, #f59e0b, #ef4444)'
    },
    {
      id: 4,
      title: 'Clipboard Manager',
      description: 'Advanced clipboard management extension with history and quick access features.',
      technologies: ['JavaScript', 'HTML', 'CSS'],
      icon: FaClipboard,
      gradient: 'linear-gradient(135deg, #3b82f6, #06b6d4)'
    },
    {
      id: 5,
      title: 'YouTube Ad Blocker',
      description: 'Efficient ad blocker specifically designed for YouTube platform.',
      technologies: ['JavaScript', 'Content Scripts'],
      icon: FaShieldAlt,
      gradient: 'linear-gradient(135deg, #ef4444, #ec4899)'
    },
    {
      id: 6,
      title: 'General Ad Blocker',
      description: 'Comprehensive ad blocking extension that works across all websites.',
      technologies: ['JavaScript', 'Filter Lists'],
      icon: FaShieldAlt,
      gradient: 'linear-gradient(135deg, #6366f1, #8b5cf6)'
    }
  ];

  return (
    <section id="projects" className="py-20 bg-gray-800">
      <div className="container">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
          viewport={{ once: true }}
          className="text-center mb-16"
        >
          <h2 className="text-4xl md:text-5xl font-bold mb-6">
            My <span className="text-gradient">Projects</span>
          </h2>
          <p className="text-xl text-gray-300 max-w-3xl mx-auto">
            A showcase of my work including Flutter mobile applications and browser extensions.
          </p>
        </motion.div>

        {/* Projects Grid */}
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
          {projects.map((project, index) => (
            <motion.div
              key={project.id}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: index * 0.1 }}
              whileHover={{ y: -10 }}
              viewport={{ once: true }}
              className="card"
              style={{ overflow: 'hidden' }}
            >
              {/* Project Icon */}
              <div style={{
                height: '12rem',
                background: project.gradient,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                position: 'relative',
                marginBottom: '1.5rem',
                borderRadius: '0.5rem'
              }}>
                <project.icon style={{ fontSize: '4rem', color: 'rgba(255, 255, 255, 0.8)' }} />
              </div>

              {/* Project Content */}
              <div>
                <h3 className="text-xl font-bold text-white mb-2">{project.title}</h3>
                <p className="text-gray-400 text-sm mb-4 leading-relaxed">
                  {project.description}
                </p>

                {/* Technologies */}
                <div className="flex flex-wrap gap-2 mb-4">
                  {project.technologies.map((tech, techIndex) => (
                    <span
                      key={techIndex}
                      style={{
                        padding: '0.25rem 0.5rem',
                        background: 'rgba(59, 130, 246, 0.2)',
                        color: '#93c5fd',
                        fontSize: '0.75rem',
                        borderRadius: '9999px',
                        border: '1px solid rgba(59, 130, 246, 0.3)'
                      }}
                    >
                      {tech}
                    </span>
                  ))}
                </div>

                {/* Action Buttons */}
                <div className="flex gap-3">
                  <motion.a
                    href="#"
                    whileHover={{ scale: 1.05 }}
                    whileTap={{ scale: 0.95 }}
                    className="btn-primary"
                    style={{
                      flex: 1,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      gap: '0.5rem',
                      fontSize: '0.875rem'
                    }}
                  >
                    <FaExternalLinkAlt style={{ fontSize: '0.75rem' }} />
                    <span>Demo</span>
                  </motion.a>
                  <motion.a
                    href="#"
                    whileHover={{ scale: 1.05 }}
                    whileTap={{ scale: 0.95 }}
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      padding: '0.5rem',
                      border: '1px solid #4b5563',
                      color: '#d1d5db',
                      borderRadius: '0.5rem',
                      transition: 'all 0.3s ease'
                    }}
                    onMouseEnter={(e) => {
                      e.target.style.borderColor = '#3b82f6';
                      e.target.style.color = '#3b82f6';
                    }}
                    onMouseLeave={(e) => {
                      e.target.style.borderColor = '#4b5563';
                      e.target.style.color = '#d1d5db';
                    }}
                  >
                    <FaGithub style={{ fontSize: '1.125rem' }} />
                  </motion.a>
                </div>
              </div>
            </motion.div>
          ))}
        </div>

        {/* Call to Action */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.4 }}
          viewport={{ once: true }}
          className="text-center"
          style={{ marginTop: '4rem' }}
        >
          <p className="text-gray-300 mb-6">
            Interested in working together or want to see more projects?
          </p>
          <motion.button
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
            onClick={() => document.querySelector('#contact')?.scrollIntoView({ behavior: 'smooth' })}
            className="btn-primary"
          >
            Get In Touch
          </motion.button>
        </motion.div>
      </div>
    </section>
  );
};

export default Projects;
